import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ENGINE_LABELS } from "@/config";
import { useFlags } from "@/lib/flags";
import { Page, PageHeader, Card, Segmented, Chip, PrivacyNote, Loading, usd, int, plural } from "@/components/common";
import { TYPE_LABEL, sessionAction, eventCost, clock } from "@/components/ActivityEvent";
import { investigatePath } from "@/components/InvestigateTimeline";

// design_handoff_crewbench/screens/Activity.dc.html: everything sessions did, by day, metadata only.
const TYPES = [["all", "All"], ["usage", "Spend"], ["commit", "Commits"], ["file", "Files"], ["report", "Reports"], ["session", "Sessions"]];
const typeOk = (t, e) => t === "all" || e.type === t || (t === "session" && e.type === "budget");
const LEVEL_WORD = { coder_daily: "daily budget", coder_monthly: "monthly budget", company: "the company's monthly budget",
                     project: "a project's monthly budget", objective: "an objective's budget" };

const dayKey = (iso) => new Date(iso).toDateString();
function dayLabel(iso) {
  const d = new Date(iso);
  const full = d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  if (d.toDateString() === today.toDateString()) return `Today · ${full}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday · ${full}`;
  return full;
}

// a session's short id; with Investigate on (FEATURE_INVESTIGATE), a link to its timeline
function SessionId({ e, short }) {
  if (!useFlags().investigate || !e.session_id) return short;
  return (
    <Link to={investigatePath("owner", e.session_id)} title="Investigate this session"
      className="text-foreground underline decoration-gr-hairline underline-offset-2 hover:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded-sm">
      {short}
    </Link>
  );
}

// the detail line: plain words, code in mono, lines and notes in their colours
function Detail({ e }) {
  const short = e.short_id || (e.session_id || "").slice(0, 6);
  const sid = <SessionId e={e} short={short} />;
  const lines = (a, r) => <> <span className="text-gr-success">+{int(a)}</span> <span className="text-gr-danger">−{int(r)}</span></>;
  switch (e.type) {
    case "usage": return <>{ENGINE_LABELS[e.engine] || e.engine} · in {int(e.input_tokens)} · out {int(e.output_tokens)} · session {sid}</>;
    case "commit": return <><span className="font-mono text-foreground">{(e.sha || "").slice(0, 7)}</span> · {e.message}</>;
    case "file": return <><span className="font-mono text-foreground">{e.path}</span>{lines(e.added, e.removed)}</>;
    case "report": return <>Session {sid} · {plural(e.files, "file")}{lines(e.added, e.removed)}{e.flags > 0 && <span className="text-gr-warning"> · {plural(e.flags, "flag")}</span>}</>;
    case "session": return <>Session {sid} {sessionAction(e)}</>;
    case "budget": return <span className="text-gr-danger">Reached {e.coder_id ? "their " : ""}{LEVEL_WORD[e.scope] || "a budget"}{e.cap_usd != null && ` of ${usd(e.cap_usd)}`}</span>;
    default: return null;
  }
}

export default function Activity() {
  const [type, setType] = useState("all");
  const [person, setPerson] = useState("all");
  const { data, isLoading } = useQuery({ queryKey: ["activity"], queryFn: async () => (await api.get("/activity")).data, refetchInterval: 6000 });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const coders = (team?.members || []).filter((m) => m.role === "coder");
  const names = Object.fromEntries((team?.members || []).map((m) => [m.id, m.name || m.email]));
  const timeline = data?.timeline || [];

  const today = new Date().toDateString();
  const spentToday = timeline.filter((e) => e.type === "usage" && dayKey(e.ts) === today);
  const total = spentToday.reduce((a, e) => a + (e.cost_usd || 0), 0);
  const sessionsToday = new Set(spentToday.map((e) => e.session_id)).size;
  const rows = timeline.filter((e) => typeOk(type, e) && (person === "all" || e.coder_id === person));
  const groups = [];
  for (const e of rows) {
    const k = dayKey(e.ts);
    if (!groups.length || groups[groups.length - 1].key !== k) groups.push({ key: k, label: dayLabel(e.ts), rows: [] });
    groups[groups.length - 1].rows.push(e);
  }
  return (
    <Page className="gap-5" data-testid="owner-activity">
      <PageHeader eyebrow="Activity · Metadata only" title="Who worked on what, and what it cost."
        status={sessionsToday ? `${usd(total)} spent today across ${plural(sessionsToday, "session")}.` : "Nothing spent today."} />
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <Segmented label="Event type" options={TYPES} value={type} onChange={setType} testid="activity-type" />
        <Segmented label="Person" options={[["all", "Everyone"], ...coders.map((m) => [m.id, (m.name || m.email).split(" ")[0]])]}
          value={person} onChange={setPerson} testid="activity-person" />
      </div>
      {isLoading ? <Loading /> : !groups.length ? (
        <Card className="px-5 py-6 text-muted-foreground">{timeline.length ? "Nothing matches these filters." : "No activity yet."}</Card>
      ) : (
        <div className="bg-card rounded-[10px] overflow-x-auto text-sm tabular-nums">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="min-w-[760px] px-4 pt-3 pb-2 text-xs uppercase tracking-[0.09em] text-muted-foreground border-b">{g.label}</div>
              {g.rows.map((e, i) => (
                <div key={i} className="min-w-[760px] grid grid-cols-[44px_84px_112px_minmax(0,1fr)_64px] gap-3 items-center px-4 py-[9px] border-b"
                  data-testid={`activity-row-${e.type}`}>
                  <span className="text-muted-foreground">{clock(e.ts)}</span>
                  <span><Chip>{TYPE_LABEL[e.type]}</Chip></span>
                  <span className="truncate">{e.coder_id ? names[e.coder_id] || "A former member" : "—"}</span>
                  <span className="text-muted-foreground min-w-0 [overflow-wrap:anywhere]"><Detail e={e} /></span>
                  <span className="text-right">{eventCost(e)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <PrivacyNote>Activity shows files, lines, commits, tokens and cost. It never shows prompts, replies or anything typed in a session.</PrivacyNote>
    </Page>
  );
}
